import {
  BookOpen,
  Users,
  Calendar,
  Bell,
  Shield,
  Cloud,
  Zap,
  CheckCircle2,
  TrendingUp,
  Clock,
  FileText,
  Settings,
  MessageSquare,
  Database,
  Lock,
  Smartphone,
  Globe,
  Award,
  Target,
  UserCheck,
  Library,
  BookMarked,
  Search,
  BarChart3,
  Mail,
  Phone,
  MapPin,
  Heart,
  Star,
  BookCheck,
  Receipt,
  AlertCircle,
} from "lucide-react";

// Hero Section Data
export const heroSection = {
  badge: "Complete Library Management Platform",
  title: "Library Management System",
  highlightedText: "Transform Your Library Operations",
  description:
    "A comprehensive, cloud-powered platform designed to streamline every aspect of library operations — from cataloging and member tracking to book borrowing, returns, and reporting. Experience smooth, secure, and centralized control over your entire library with cutting-edge technology.",
  cta: {
    primary: "Schedule Demo",
    secondary: "Explore Features",
  },
  stats: [
    { value: "200+", label: "Libraries Trust Us" },
    { value: "1M+", label: "Books Managed" },
    { value: "99.9%", label: "Uptime Guarantee" },
    { value: "24/7", label: "Support Available" },
  ],
};

// Challenges Section
export const challenges = {
  title: "The Challenges",
  highlightedText: "Libraries Face",
  description:
    "Libraries today struggle with manual processes, outdated systems, and scattered records. This not only slows down daily operations but also leads to frequent errors, poor tracking, and reduced user satisfaction.",
  cards: [
    {
      icon: FileText,
      title: "Manual Book Tracking",
      description:
        "Libraries still using paper-based logs or spreadsheets for cataloging, issuing, and returning books face frequent data errors, misplaced records, and tracking delays. Manual workflows consume time and limit accountability.",
      gradient: "from-red-500/10 to-orange-500/10",
      iconColor: "text-red-500",
    },
    {
      icon: Database,
      title: "Lack of Centralized System",
      description:
        "Without an integrated platform, managing inventory, users, due dates, and fines becomes complex. Scattered data makes it difficult to make timely decisions or access accurate records.",
      gradient: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-500",
    },
    {
      icon: Settings,
      title: "Inefficient Processes",
      description:
        "Traditional workflows and disconnected tools hinder operations and create confusion. From new member onboarding to overdue tracking, inefficiencies impact both staff productivity and user experience.",
      gradient: "from-purple-500/10 to-pink-500/10",
      iconColor: "text-purple-500",
    },
  ],
};

// Solution Features
export const solutionFeatures = {
  title: "Why Our",
  highlightedText: "Library Management System",
  description:
    "Built by Oethical Technology, our Library Management System centralizes your library operations in one user-friendly platform — making it easier to manage books, users, and circulation efficiently and securely.",
  features: [
    {
      icon: Cloud,
      title: "Cloud-Based, Accessible Anytime",
      description:
        "Access your entire library system from anywhere. Our cloud-based platform ensures real-time updates, no installations, and flexible access on all devices. Automatic backups and seamless synchronization keep your data safe and accessible.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: Zap,
      title: "All-in-One Centralized System",
      description:
        "Manage book inventory, members, issuing, and returns in one seamless system—no more switching tools or dealing with duplicate entries. Streamlined workflows reduce errors and save valuable time.",
      gradient: "from-yellow-500/20 to-orange-500/20",
      iconColor: "text-yellow-500",
    },
    {
      icon: Shield,
      title: "Secure, Reliable & Easy to Use",
      description:
        "Built with modern security protocols and designed for ease of use, our system keeps your library data safe and your staff comfortable with minimal training. GDPR compliant with role-based access controls.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
  ],
};

// Module Features
export const moduleFeatures = {
  title: "Explore Our Powerful",
  highlightedText: "Module Features",
  description:
    "Comprehensive modules designed to handle every aspect of library management efficiently and effectively.",
  modules: [
    {
      id: "books-management",
      title: "Books Management",
      icon: BookOpen,
      description:
        "Organize and monitor your entire book collection like a pro. Complete cataloging system with advanced search capabilities.",
      color: "text-cyan-500",
      bgGradient: "from-cyan-500/10 to-blue-500/10",
      features: [
        "Store all books with complete details—from title and author to category and price",
        "Track total books, issued copies, and currently available stock in real-time",
        "Maintain a clean, searchable inventory to save time and avoid confusion",
        "Barcode and ISBN support for quick book identification",
      ],
      details:
        "A smarter way to manage your library's books in one place. Advanced cataloging features, bulk import options, and comprehensive book details ensure your collection is always organized and accessible.",
    },
    {
      id: "member-directory",
      title: "Member Directory",
      icon: Users,
      description:
        "Manage all library members effortlessly in one place. Complete member management system with role-based access.",
      color: "text-blue-500",
      bgGradient: "from-blue-500/10 to-indigo-500/10",
      features: [
        "Create and update member profiles with ease",
        "Store key details like member type (student/staff), joining date, and more",
        "Keep your member database organized and up to date",
        "Track member borrowing history and preferences",
      ],
      details:
        "Everything you need to track and manage library users. Comprehensive member profiles, borrowing history, and membership management tools help you maintain accurate records and provide better service.",
    },
    {
      id: "issue-return",
      title: "Issue & Return Tracking",
      icon: BookCheck,
      description:
        "Simplify issuing and returning books with smart automation. Real-time tracking and automated fine calculations.",
      color: "text-green-500",
      bgGradient: "from-green-500/10 to-emerald-500/10",
      features: [
        "Issue books with complete info—member details, due dates, and fine rules",
        "Auto-calculate late return charges and update book stock in real-time",
        "Ensure transparency and accountability in every transaction",
        "Quick return processing with automatic stock updates",
      ],
      details:
        "Faster book issues and returns with zero manual errors. Automated workflows, real-time stock updates, and comprehensive transaction history ensure smooth operations and accurate records.",
    },
    {
      id: "smart-management",
      title: "Smart Management",
      icon: Smartphone,
      description:
        "Manage operations from web or mobile. Easy navigation and responsive UI built for modern libraries.",
      color: "text-purple-500",
      bgGradient: "from-purple-500/10 to-pink-500/10",
      features: [
        "Web & mobile compatible — access from any device",
        "Dark/light mode & RTL support for user preference",
        "Customizable views and personalized dashboards",
        "Offline mode support for mobile app",
      ],
      details:
        "Access your system from anywhere — whether you're at the desk or remote. Stay connected to your library in real-time with responsive design and mobile-first approach.",
    },
    {
      id: "notifications",
      title: "Smart Notifications",
      icon: Bell,
      description:
        "Keep your members informed—automatically and effortlessly. Multi-channel notification system.",
      color: "text-orange-500",
      bgGradient: "from-orange-500/10 to-amber-500/10",
      features: [
        "Send timely notifications without any manual effort",
        "Notify members about issued books, due date reminders, and return confirmations via email",
        "Reduce late returns and improve user engagement with consistent communication",
        "Customizable notification templates and scheduling",
      ],
      details:
        "Automated alerts ensure no one misses an update—saving time and avoiding confusion. Multi-channel notifications (email, SMS, in-app) keep members informed and engaged.",
    },
    {
      id: "reports",
      title: "Reports & Analytics",
      icon: BarChart3,
      description:
        "Get clear insights to support smarter library decisions. Comprehensive reporting and analytics dashboard.",
      color: "text-teal-500",
      bgGradient: "from-teal-500/10 to-cyan-500/10",
      features: [
        "Book circulation reports with trends and patterns",
        "Member activity reports and borrowing statistics",
        "Overdue books and fine collection reports",
        "Custom report builder for specific requirements",
      ],
      details:
        "Powerful reporting tools that help you make data-driven decisions. Generate comprehensive library reports, analyze trends, track popular books, and export data in multiple formats for further analysis.",
    },
    {
      id: "search",
      title: "Advanced Search",
      icon: Search,
      description:
        "Powerful search capabilities to find books and resources quickly. Multi-criteria search with filters.",
      color: "text-indigo-500",
      bgGradient: "from-indigo-500/10 to-purple-500/10",
      features: [
        "Search by title, author, ISBN, category, or keywords",
        "Advanced filters for precise book discovery",
        "Save search preferences and recent searches",
        "Quick access to popular and recommended books",
      ],
      details:
        "Find what you need instantly with our advanced search system. Multi-criteria search, smart filters, and personalized recommendations help members discover books quickly and easily.",
    },
    {
      id: "support",
      title: "Support & Help",
      icon: MessageSquare,
      description:
        "Get quick help from Oethical Technology's expert support team. Multiple support channels available.",
      color: "text-pink-500",
      bgGradient: "from-pink-500/10 to-rose-500/10",
      features: [
        "Live chat & email support for instant assistance",
        "Onboarding & documentation for smooth setup",
        "Issue tracking & resolution for technical problems",
        "Knowledge base and video tutorials",
      ],
      details:
        "Whether you're onboarding or need help mid-operation, our support ensures your library runs without interruptions. Dedicated support channels and comprehensive resources help you get the most out of the system.",
    },
  ],
};

// Roles Section
export const roles = {
  title: "Helping Everyone",
  highlightedText: "Engage Better",
  description:
    "LMS is designed to empower every stakeholder in your library with the tools they need to manage operations efficiently.",
  roles: [
    {
      icon: Library,
      title: "Libraries",
      description:
        "Empower your institution with the Library Management System from Oethical Technology. Streamline cataloging, user management, reporting, and notifications — all from a centralized dashboard.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: Users,
      title: "Librarians",
      description:
        "Simplify tasks such as issuing books, tracking returns, updating catalogs, and sending reminders. Save time and reduce errors with automated tools.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
    {
      icon: BookMarked,
      title: "Members",
      description:
        "Easily check book availability, borrow history, and due dates. Get personalized reminders and explore digital catalogs from anywhere.",
      gradient: "from-purple-500/20 to-pink-500/20",
      iconColor: "text-purple-500",
    },
    {
      icon: Globe,
      title: "Guests",
      description:
        "Browse available resources, request temporary access, and stay updated on events, announcements, and new arrivals.",
      gradient: "from-orange-500/20 to-red-500/20",
      iconColor: "text-orange-500",
    },
  ],
};

// Pricing Plans
export const pricingPlans = {
  title: "Our",
  highlightedText: "Pricing Plans",
  description:
    "Flexible pricing designed to suit libraries of all sizes. Only pay for the features you need.",
  plans: [
    {
      name: "Basic",
      price: "Free",
      period: "forever",
      description: "Perfect for small libraries getting started",
      features: [
        "Up to 1,000 books",
        "Basic cataloging system",
        "Manual member records",
        "Limited notifications",
        "Basic reports",
        "Cloud access",
        "Email support",
      ],
      cta: "Get Started",
      popular: false,
      gradient: "from-gray-500/10 to-gray-600/10",
    },
    {
      name: "Professional",
      price: "₹14,999",
      period: "per year",
      description: "Ideal for growing libraries",
      features: [
        "Up to 10,000 books",
        "All Basic features",
        "Advanced book management",
        "Overdue automation",
        "Role-based access",
        "Detailed reporting & analytics",
        "Priority support",
        "Training sessions",
      ],
      cta: "Start Free Trial",
      popular: true,
      gradient: "from-primary/20 to-primary/10",
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large libraries and multi-branch",
      features: [
        "Unlimited books",
        "All Professional features",
        "Multi-branch support",
        "Custom workflows",
        "Unlimited users & records",
        "API integrations",
        "Dedicated account manager",
        "24/7 premium support",
        "Custom development",
      ],
      cta: "Contact Sales",
      popular: false,
      gradient: "from-purple-500/20 to-pink-500/20",
    },
  ],
};

// Registration Steps
export const registrationSteps = {
  title: "Process to",
  highlightedText: "Register & Start",
  description:
    "Get started with LMS in just a few simple steps. Our streamlined onboarding process ensures you're up and running quickly.",
  steps: [
    {
      number: "01",
      title: "Sign Up",
      description:
        "Create your account and gain instant access to the cloud-based Library Management System. Our team will verify your details and activate your account within 24 hours.",
      icon: UserCheck,
    },
    {
      number: "02",
      title: "Set Up Your Library",
      description:
        "Input your library's name, location, operating hours, and roles to configure the system to your workflow. Our guided setup wizard makes this process simple.",
      icon: Settings,
    },
    {
      number: "03",
      title: "Import Books and Members",
      description:
        "Upload your book inventory and member data easily using CSV files or manual entry. Our bulk import feature saves you time and effort.",
      icon: Database,
    },
    {
      number: "04",
      title: "Start Managing",
      description:
        "Begin issuing books, tracking returns, sending notifications, and generating reports from your dashboard. Our support team is available to help you get started.",
      icon: CheckCircle2,
    },
  ],
};

// FAQs
export const faqs = {
  title: "Frequently Asked",
  highlightedText: "Questions",
  description:
    "At Oethical Technology, we build intuitive, secure, and scalable digital solutions to help libraries succeed. Here are some answers to commonly asked questions.",
  questions: [
    {
      question: "Is this system suitable for all types of libraries?",
      answer:
        "Yes! Our Library Management System is built to serve public, academic, and private libraries. It scales according to your size and needs. Whether you're a small community library or a large university library, our system adapts to your requirements.",
    },
    {
      question: "Is any installation required?",
      answer:
        "No. The system is fully cloud-based and works from any modern web browser. No installations, just instant access. You can also use our mobile apps for iOS and Android for on-the-go library management.",
    },
    {
      question: "Can we import our existing data?",
      answer:
        "Absolutely. We help migrate your existing catalog, user records, and borrowing logs from spreadsheets or other systems. Our support team will guide you through the migration process and ensure all your data is transferred accurately and securely.",
    },
    {
      question: "Is our data secure?",
      answer:
        "Yes. Your library data is encrypted, securely stored, and protected by role-based access. Regular backups ensure your records are never lost. We comply with data protection regulations and conduct regular security audits to keep your information safe.",
    },
    {
      question: "Is training provided for staff?",
      answer:
        "Yes. We offer detailed training sessions, guides, video tutorials, and ongoing support to help your team get started and become proficient quickly. Professional and Enterprise plans include dedicated training sessions.",
    },
    {
      question: "Can I request a live demo?",
      answer:
        "Definitely. Just contact us to schedule a personalized demo of our Library Management System. We'll walk you through all the features and show you how the system can benefit your library specifically.",
    },
    {
      question: "Does LMS support barcode scanning?",
      answer:
        "Yes, our system supports barcode and ISBN scanning for quick book identification and checkout. This feature is available in Professional and Enterprise plans, making book management faster and more efficient.",
    },
    {
      question: "Can we customize the system to our needs?",
      answer:
        "Yes, our Enterprise plan includes custom development and workflow customization. We can tailor the system to match your specific library processes and requirements. Contact our sales team to discuss your customization needs.",
    },
  ],
};

// Benefits/Stats Section
export const benefits = {
  title: "Why Libraries Choose",
  highlightedText: "LMS",
  stats: [
    {
      icon: TrendingUp,
      value: "80%",
      label: "Time Saved on Operations",
      description: "Automate repetitive tasks and focus on service",
      color: "text-green-500",
    },
    {
      icon: Award,
      value: "200+",
      label: "Libraries Trust Us",
      description: "Join hundreds of satisfied institutions",
      color: "text-blue-500",
    },
    {
      icon: Target,
      value: "99.9%",
      label: "Uptime Guarantee",
      description: "Reliable service you can count on",
      color: "text-purple-500",
    },
    {
      icon: Clock,
      value: "24/7",
      label: "Support Available",
      description: "Get help whenever you need it",
      color: "text-orange-500",
    },
  ],
};
